import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Hidden } from '@material-ui/core';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { apiErrorClear } from '../../../../../global/ducks/base-api';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { ApiStatuses } from '../../../../../global/models/api-model';
import { ArcRoutes } from '../../../constants/routes';
import { EmploymentState, ScheduledHours } from '../../employment/employment-model';
import { getCurrentEmploymentItem, getEmploymentItemById } from '../../employment/employment-selectors';
import { setDecreaseInScheduledHoursStatus, setUpdateScheduledHoursStatus } from '../../employment/employment-slice';
import { EmploymentInfo } from '../individual-compensation/employment-info';
import { ScheduledHoursForm } from './scheduled-hours-form';
import { ScheduledHoursFormValues } from './scheduled-hours-form-model';
import { patchScheduledHoursRequest, postScheduledHoursRequest } from './scheduled-hours-requests';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { TcsmEpiApiState, TcsmStates } from '../../../../../root/tcsm-model';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { useLockedPageState } from '../../../../../global/hooks/useLockedPageState';
import { LockedPageKeys } from '../../../../../global/models/locked-page-model';
import { ProcessName, RegisterStartProcess, RegisterCompletedProcess } from '../../../../../global/helpers/analytics-helper';
import { NewHireEnrollmentRoutes } from '../../new-hire-enrollment/models/new-hire-routes';
import { FormTypes } from '../../../../account-info/models/form-models';
import { isDioAdmin, isInstitutionOfficer } from '../../../../../global/helpers/auth-helpers';
import { useNewHireEnrollmentAllowed } from '../../new-hire-enrollment/hooks/useNewhireEnrollmentAllowed';
import { clearNewHireEnrollmentOptions } from '../../new-hire-enrollment/slice/new-hire-enrollment-slices';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { getGpsType } from '../../../../../global/helpers/gps-helper';
import { getAllExistingEnrollment } from '../../../../../global/ducks/mlps-api';

export function ScheduledHoursApp() {
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const employmentState = useSelector<TcsmStates, EmploymentState>(state => state.employmentState);
	const basePath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.Compensation}`);
	const employmentPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.Employment}`);
	const dispatch = useDispatch();
	const history = useHistory();
	const roleAllowed = isDioAdmin() || isInstitutionOfficer();
	const newHireEnrollmentAllowed = useNewHireEnrollmentAllowed();
	const enrollUrl = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.UpdateEnrollment}${NewHireEnrollmentRoutes.WhoWillEnroll}`);
	const ENROLLMENT_HOURS_THRESHOLD = 1000; // Unclear if this needs to be API driven, hard-coded for now
	const gpsItem = getGpsType(null, null);

	const lockedState = useLockedPageState(LockedPageKeys.Compensation);
	RegisterStartProcess(ProcessName.ScheduledHours);

	useEffect(() => {
		dispatch(apiErrorClear());
		dispatch(setUpdateScheduledHoursStatus(ApiStatuses.None));
		dispatch(setDecreaseInScheduledHoursStatus(ApiStatuses.None));

	}, []);

	const { scheduledHoursId } = useParams<any>();

	const currentScheduledHours = (scheduledHoursId
		? getEmploymentItemById(employmentState?.currentEmployment?.scheduled_hours, scheduledHoursId)
		: getCurrentEmploymentItem(employmentState?.currentEmployment?.scheduled_hours)) as ScheduledHours;

	function enrollmentAllowed() {
		const isStip = employmentState?.currentEmployment?.id > 0;
		return isStip && newHireEnrollmentAllowed && roleAllowed;
	}

	const handleSubmit = (initialValues: ScheduledHoursFormValues, formValues: ScheduledHoursFormValues) => {
		const onSuccess = () => {
			RegisterCompletedProcess(ProcessName.ScheduledHours);

			dispatch(getAllExistingEnrollment(employmentState?.currentEmployee?.guid, (res) => {
				const shouldGoToEnrollment = Number(initialValues?.hours) < ENROLLMENT_HOURS_THRESHOLD && Number(formValues?.hours) >= ENROLLMENT_HOURS_THRESHOLD && enrollmentAllowed;

				if (shouldGoToEnrollment && res?.data?.length === 0) {
					dispatch(clearNewHireEnrollmentOptions());
					setTimeout(() => {
						history.push(enrollUrl, {
							date: formValues.start_date,
						}); // TODO:  Right now just using same path as NH enrollment, do we need a duplicate version?
					}, 2000);
				}

			}));


		};

		if (scheduledHoursId) {
			patchScheduledHoursRequest(dispatch,
				employmentState?.currentEmployee?.id,
				employmentState?.currentEmployee?.employment_id,
				initialValues,
				formValues,
				currentScheduledHours,
				onSuccess(),
			);
		} else {
			postScheduledHoursRequest(dispatch,
				employmentState?.currentEmployee?.id,
				employmentState?.currentEmployee?.employment_id,
				initialValues,
				formValues,
				currentScheduledHours,
				onSuccess(),
			);
		}
	};



	if (lockedState.isLocked) {
		history.push(employmentPath);
	}

	// redirect to individual compensation page on success with query string
	if (employmentState?.updateScheduledHoursStatus === ApiStatuses.Success) {
		return (<Redirect to={{
			pathname: `${basePath}`,
			search: `?success=${FormTypes.SCHEDULED_HOURS}`,
		}} />);
	}

	if (employmentState?.decreaseInSchduledHourStatus === ApiStatuses.Success) {
		return (<Redirect to={{
			pathname: `${basePath}`,
			search: `?success=${FormTypes.SCHEDULED_HOURS_BELOW_1000}`,
		}} />);
	}
	return (
		<>
			<ScheduledHoursForm
				onSubmitHandler={handleSubmit}
				currentScheduledHours={currentScheduledHours}
				isEdit={!!scheduledHoursId}
				previousRoute={basePath}
				compensationProps={epiProps?.compensationSettingsState?.epiContent}
				labelSettings={epiProps?.labelSettingState?.epiContent}
			>
				<PageHeader
					headerText={epiProps.compensationSettingsState.epiContent.editScheduledHoursTitle}
					description={epiProps.compensationSettingsState.epiContent.editScheduledHoursPageDescription} />
				<Hidden mdUp>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
					</SideBarContent>
				</Hidden>
				<DividerSection>
					<EmploymentInfo
						employmentData={employmentState?.currentEmployment}
					/>
				</DividerSection>
			</ScheduledHoursForm>

		</>
	);
}